/**
 * Phone Number Screen for Ramps Flow
 *
 * Dedicated screen for entering phone number
 * Part of the sequential flow: Calculator → Name → Phone → Address → ID → Review → Status
 */

import { t } from "@lingui/core/macro";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { CircleFlag } from "react-circle-flags";
import type {
	PhonePreCheckRequest,
	SenderProfile,
} from "@/api/types/moneygram";
import { RampsButton } from "@/components/ramps/Button";
import { isBankService } from "@/domain/calculator";
import { useApiClientWithAutoVersion } from "@/hooks/useApiClientWithAutoVersion";
import { useContinue } from "@/hooks/useContinue";
import { useNavigateBack } from "@/hooks/useNavigateBack";
import { useAnalytics } from "@/hooks/usePostHog";
import { getAuthTokenFromSearchParams } from "@/lib/ramps-search-schema";
import { RampsInput } from "../components/ramps/Input";
import { RampsSelectBottomSheet } from "../components/ramps/RampsSelectBottomSheet";
import { RampsText } from "../components/ramps/Text";
import { Progress } from "../components/ui/progress";
import {
	phoneStepSchema,
	phoneVerificationSchema,
	useRampsForm,
} from "../hooks/useRampsForm";
import { extractKycDataFromCompliance } from "../stores/ramps-store";
import { rampsActions, useRampsStore } from "../stores/use-ramps-store";
import type { PhoneCountryOption } from "../utils/phoneCountryOptions";
import {
	buildPhoneCountryOptions,
	findPhoneCountryByIso3,
} from "../utils/phoneCountryOptions";
import {
	FIRST_NAME_FIELD_MAX,
	NAME_FIELD_MAX,
	sanitizeNameInput,
} from "../utils/senderNames";

function PhoneNumberPage() {
	const search = Route.useSearch();
	const store = useRampsStore();
	const form = useRampsForm();
	const handleContinue = useContinue();
	const handleBack = useNavigateBack();
	const { trackPageView, trackEvent } = useAnalytics();

	// Track page view
	useEffect(() => {
		trackPageView("phone", { page_name: "phone" });
	}, [trackPageView]);

	// Get API client with authentication (supports both v2 and v5 API)
	const authToken = getAuthTokenFromSearchParams(search) || store.token;
	const { apiClient } = useApiClientWithAutoVersion({
		authToken,
	});

	// Fetch countries using the same logic as calculator
	const countriesQuery = useQuery({
		queryKey: ["countries"],
		queryFn: async () => {
			const countries = await apiClient.referenceData.getCountries();
			return countries;
		},
		enabled: false,
	});

	// Create phone country options using actual phoneDialCodes from API.
	// Deliberately NOT filtered by cashOutCountries/cashInCountries: a user's
	// phone country is independent of where Ramps can pay out (RAMPS-4425).
	const phoneCountryOptions: PhoneCountryOption[] = useMemo(
		() => buildPhoneCountryOptions(countriesQuery.data || []),
		[countriesQuery.data],
	);

	// Token and transaction_id are synced globally by URLParamsInitializer

	// Set initial phone country from payout country
	useEffect(() => {
		const hasCountries = phoneCountryOptions.length > 0;
		const hasPayoutCountry = !!store.payoutCountry;

		// Only initialize if store doesn't have a selection yet
		if (!store.phoneCountrySelection && hasCountries && hasPayoutCountry) {
			// Try to find phone country option matching payout country.
			// Fall back to USA (+1) rather than the alphabetically first country,
			// since the list now covers every country (RAMPS-4425).
			const phoneCountryForPayout = findPhoneCountryByIso3(
				store.payoutCountry,
				phoneCountryOptions,
			);
			const initial =
				phoneCountryForPayout ||
				findPhoneCountryByIso3("USA", phoneCountryOptions) ||
				phoneCountryOptions[0];

			if (initial) {
				// Update store instead of URL
				rampsActions.setPhoneCountrySelection(initial.value);
				form.setFieldValue("phoneCountryCode", initial.value);
			}
		}
	}, [
		store.phoneCountrySelection,
		store.payoutCountry,
		phoneCountryOptions,
		form,
	]);

	// Initialize store phoneCountrySelection from existing phoneCountryCode if needed
	useEffect(() => {
		if (
			store.phoneCountryCode &&
			!store.phoneCountrySelection &&
			phoneCountryOptions.length > 0
		) {
			// Try to find matching option from existing dial code
			const dialCodeWithoutPlus = store.phoneCountryCode.replace("+", "");
			const matchingOption = phoneCountryOptions.find(
				(opt) => opt.dialCode === dialCodeWithoutPlus,
			);

			if (matchingOption) {
				rampsActions.setPhoneCountrySelection(matchingOption.value);
				form.setFieldValue("phoneCountryCode", matchingOption.value);
			}
		}
	}, [
		store.phoneCountryCode,
		store.phoneCountrySelection,
		phoneCountryOptions,
		form,
	]);

	// Sync form field with store selection
	useEffect(() => {
		if (store.phoneCountrySelection && phoneCountryOptions.length > 0) {
			const selectedOption = phoneCountryOptions.find(
				(opt) => opt.value === store.phoneCountrySelection,
			);
			if (
				selectedOption &&
				form.state.values.phoneCountryCode !== selectedOption.value
			) {
				form.setFieldValue("phoneCountryCode", selectedOption.value);
			}
		}
	}, [store.phoneCountrySelection, phoneCountryOptions, form]);

	// Helper to format phone numbers
	const formatPhoneNumber = (
		value: string,
		countryCodeValue: string | undefined,
	) => {
		// Clean input - only allow digits
		const cleaned = value.replace(/\D/g, "");

		// Extract dial code from form value (format: "dialCode-iso3Code" or just dial code)
		// e.g., "1-USA" -> "1", or "+1" -> "1"
		// Handle undefined/empty countryCodeValue gracefully
		if (!countryCodeValue) {
			// Default to 15 digits if country code not set yet
			return cleaned.slice(0, 15);
		}

		const dialCode = countryCodeValue.includes("-")
			? countryCodeValue.split("-")[0]
			: countryCodeValue.replace("+", "");

		// Flexible length based on country - most countries allow 7-15 digits
		// US/Canada (+1) allows 10 digits
		const maxLength = dialCode === "1" ? 10 : 15;
		return cleaned.slice(0, maxLength);
	};

	// Helper function to capitalize first letter of each word
	const capitalizeFirstLetter = (value: string) => {
		return value
			.split(" ")
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
			.join(" ");
	};

	// Combined helper for name fields: strip API-disallowed characters
	// (digits, punctuation, symbols) then capitalize each word.
	const formatNameInput = (value: string) => {
		return capitalizeFirstLetter(sanitizeNameInput(value));
	};

	// Helper to format idLast4 (alphanumeric, max 4 characters)
	const formatIdLast4 = (value: string) => {
		// Only allow alphanumeric characters
		const cleaned = value.replace(/[^0-9A-Za-z]/g, "");
		// Max 4 characters
		return cleaned.slice(0, 4).toUpperCase();
	};

	// Helper function to get first error message from field
	const getFieldError = (
		fieldName:
			| keyof typeof phoneStepSchema.shape
			| keyof typeof phoneVerificationSchema.shape,
	) => {
		const field = form.getFieldMeta(fieldName);
		if (field?.errors && field.errors.length > 0) {
			const error = field.errors[0];
			return typeof error === "string" ? error : error.message || String(error);
		}
		return undefined;
	};

	// Phone validator that explicitly returns undefined when valid
	// This ensures errors clear immediately when validation passes
	// TanStack Form requires validators to return undefined (not null or empty array) to clear errors
	const phoneValidator = ({ value }: { value: unknown }) => {
		const result = phoneStepSchema.shape.phone.safeParse(value);
		// Explicitly return undefined when valid to clear errors immediately
		if (result.success) {
			return undefined;
		}
		// Return the first error message when invalid
		// Zod errors use 'issues' array, not 'errors'
		return result.error.issues[0]?.message;
	};

	// Loading state for phone pre-check
	const [isCheckingProfile, setIsCheckingProfile] = useState(false);
	// State for returning user verification
	const [showVerificationFields, setShowVerificationFields] = useState(false);
	const [isVerificationRequired, setIsVerificationRequired] = useState(false);
	const [isIdVerified, setIsIdVerified] = useState(false);
	const [verificationError, setVerificationError] = useState<string | null>(
		null,
	);

	/**
	 * Performs digital offramp profile lookup by phone number.
	 * Returns whether the submit flow should proceed.
	 */
	const performDigitalOfframpLookup = async (
		dialCode: string,
		phoneNumber: string,
	): Promise<boolean> => {
		setVerificationError(null);
		setIsCheckingProfile(true);

		let verificationRequired = isVerificationRequired;
		let idVerified = isIdVerified;

		try {
			// Build payload
			const payload: PhonePreCheckRequest = {
				phoneNumber,
				countryDialCode: dialCode,
			};

			if (showVerificationFields) {
				const verificationValidation = phoneVerificationSchema.safeParse(
					form.state.values,
				);

				if (!verificationValidation.success) {
					setIsCheckingProfile(false);
					return false;
				}

				payload.firstName = form.state.values.firstName;
				payload.lastName = form.state.values.lastName;
				payload.idLast4 = form.state.values.idLast4;
			}

			const preCheckResponse =
				await apiClient.profile.checkProfileByPhone(payload);

			if (
				preCheckResponse.digitalProfileId &&
				preCheckResponse.consumer.firstName
			) {
				idVerified = true;
				setIsIdVerified(true);

				rampsActions.setSenderProfile(
					preCheckResponse.consumer as SenderProfile,
				);
				// Check for step-up requirement and extract KYC data
				const kycData = extractKycDataFromCompliance(preCheckResponse);
				if (kycData) {
					rampsActions.setKycData(
						kycData.kycToken,
						kycData.kycApplicantId,
						"api",
					);

					// Track step-up requirement in analytics
					trackEvent("step_up_required", {
						source: "phone_lookup",
						action_provider: preCheckResponse.actionProvider || "",
					} as Record<string, unknown>);
				}
				if ("isStepUpRequired" in preCheckResponse) {
					rampsActions.setStepUpRequired(
						preCheckResponse.isStepUpRequired || false,
					);
				}
				if ("isAllowedToProceed" in preCheckResponse) {
					rampsActions.setIsAllowedToProceed(
						preCheckResponse.isAllowedToProceed || false,
					);
				}
				if (preCheckResponse.digitalProfileId) {
					// Existing user found by phone and ID verified
					rampsActions.setDigitalProfile(
						preCheckResponse.digitalProfileId,
						"phone",
					);

					// Fetch bank accounts for this profile
					const bankAccounts = await apiClient.profile.getProfileBankAccounts(
						preCheckResponse.digitalProfileId,
					);

					if (bankAccounts.bankAccounts.length > 0) {
						rampsActions.setSelectedBankAccount(bankAccounts.bankAccounts[0]);
					}
				}
			} else if (preCheckResponse.digitalProfileId) {
				setShowVerificationFields(true);
				setIsVerificationRequired(true);
				verificationRequired = true;
			}

			// Exit early if verification is required and ID is not verified
			if (verificationRequired && !idVerified) {
				console.warn("*** verification required and id not verified ***");
				return false;
			}

			rampsActions.setPhoneLookupCompleted(true);
			return true;
		} catch (error) {
			if (verificationRequired) {
				setVerificationError(
					t`We're sorry, but the details provided don't match an existing account for this phone number. We're unable to process your transaction at this time.`,
				);
				return false; // Exit early to prevent further processing
			}
			console.error(error);
			return true; // Continue on non-verification errors
		} finally {
			setIsCheckingProfile(false);
		}
	};

	const handleSubmit = async () => {
		// Validate current step before proceeding
		const stepValidation = phoneStepSchema.safeParse(form.state.values);

		if (!stepValidation.success) {
			return;
		}

		// Use store as source of truth for phone country
		const phoneCountryValue =
			store.phoneCountrySelection || form.state.values.phoneCountryCode;
		const selectedCountry = phoneCountryOptions.find(
			(opt) => opt.value === phoneCountryValue,
		);

		// For digitalOfframp services, check for existing profile by phone
		if (
			isBankService(store.serviceOption) &&
			authToken &&
			!store.digitalProfileId &&
			!store.phoneLookupCompleted &&
			store.operation !== "deposit"
		) {
			const dialCode = (selectedCountry?.dialCode || phoneCountryValue).replace(
				"+",
				"",
			);

			const shouldProceed = await performDigitalOfframpLookup(
				dialCode,
				form.state.values.phone,
			);

			if (!shouldProceed) {
				return;
			}
		}

		// Save phone data to store
		rampsActions.setPhoneData({
			phone: form.state.values.phone,
			phoneCountryCode: selectedCountry?.dialCode || "+1",
			phoneCountrySelection: phoneCountryValue,
		});

		rampsActions.setCurrentStep("name");
		handleContinue(search);
	};

	return (
		<div className="flex min-h-dvh flex-col bg-ramps-background-base">
			{/* Header */}
			<div className="flex items-center justify-between p-4">
				<button
					type="button"
					onClick={handleBack}
					className="flex h-8 w-8 items-center justify-center transition-opacity hover:opacity-80"
					aria-label="Back"
				>
					<ArrowLeft className="h-6 w-6 text-ramps-text-primary" />
				</button>
			</div>

			{/* Progress bar */}
			<div className="mb-8 px-4">
				<Progress
					value={25}
					className="h-1 bg-ramps-background-surface-2 *:bg-ramps-text-primary"
				/>
			</div>

			{/* Content */}
			<div className="flex flex-1 flex-col px-4">
				<div className="mb-2 text-left">
					<RampsText variant="title-lg-bold" color="primary">
						{t`Your phone number`}
					</RampsText>
				</div>

				{/* Phone Form */}
				<div className="flex-1 space-y-6">
					{/* Phone Section */}
					<div className="space-y-4">
						{/* Phone Input Field - Single instance for input and error display */}
						<form.Field
							name="phone"
							validators={{
								// Custom validator that explicitly returns undefined when valid
								// This ensures errors clear immediately when validation passes
								onChange: phoneValidator,
								onBlur: phoneValidator,
							}}
						>
							{(phoneField) => {
								const error = phoneField.state.meta.errors[0];
								return (
									<div className="space-y-2">
										<div className="flex gap-3">
											{/* Country Selector */}
											<form.Field
												name="phoneCountryCode"
												validators={{
													onChange: phoneStepSchema.shape.phoneCountryCode,
												}}
											>
												{(countryField) => {
													// Use store as source of truth, fallback to form field
													const currentValue =
														store.phoneCountrySelection ||
														countryField.state.value;
													const selectedCountry = phoneCountryOptions.find(
														(c) => c.value === currentValue,
													);

													// Convert phone country options to RampsSelectOption format
													// Include dial code in label for display
													const selectOptions = phoneCountryOptions.map(
														(country) => ({
															value: country.value,
															label: `+${country.dialCode} ${country.label}`,
															countryCode: country.countryCode,
															// Store dialCode in a way we can access it
															// We'll extract it from the label or use a custom renderer
														}),
													);

													// Handle loading/error states
													if (countriesQuery.isLoading) {
														return (
															<div className="shrink-0 rounded-lg bg-ramps-background-surface-2 p-2 pt-1">
																<div className="mt-2 flex items-center gap-2">
																	<div className="h-6 w-6 animate-pulse rounded-full bg-gray-300" />
																	<RampsText
																		variant="body-default"
																		color="primary"
																		className="font-medium"
																	>
																		...
																	</RampsText>
																</div>
															</div>
														);
													}

													if (
														countriesQuery.error ||
														phoneCountryOptions.length === 0
													) {
														return (
															<div className="shrink-0 rounded-lg bg-ramps-background-surface-2 p-2 pt-1">
																<RampsText
																	variant="body-sm"
																	color="secondary"
																	className="mt-2"
																>
																	{countriesQuery.error
																		? t`Error loading countries`
																		: t`No countries available`}
																</RampsText>
															</div>
														);
													}

													return (
														<div className="shrink-0 rounded-lg bg-ramps-background-surface-2 p-2 pt-1">
															<RampsSelectBottomSheet
																label=""
																sheetTitle={t`Select country`}
																options={selectOptions}
																value={currentValue}
																onSelect={(value) => {
																	// Update store and form
																	rampsActions.setPhoneCountrySelection(value);
																	countryField.handleChange(value);
																}}
																size="full"
																searchPlaceholder={t`Search countries...`}
																renderOptionIcon={(option) => {
																	// Extract country code from option
																	const country = phoneCountryOptions.find(
																		(c) => c.value === option.value,
																	);
																	return country?.countryCode ? (
																		<CircleFlag
																			countryCode={country.countryCode}
																			className="h-6 w-6"
																		/>
																	) : null;
																}}
																renderTriggerContent={() => {
																	if (!selectedCountry) {
																		return (
																			<div className="flex items-center gap-2">
																				<RampsText
																					variant="body-default"
																					color="disabled"
																				>
																					+1
																				</RampsText>
																			</div>
																		);
																	}
																	return (
																		<div className="flex items-center gap-2">
																			<CircleFlag
																				countryCode={
																					selectedCountry.countryCode || "us"
																				}
																				className="h-6 w-6"
																			/>
																			<RampsText
																				variant="body-default"
																				color="primary"
																				className="font-medium"
																			>
																				{`+${selectedCountry.dialCode}`}
																			</RampsText>
																		</div>
																	);
																}}
																triggerClassName="bg-transparent border-none shadow-none py-2 px-3 mt-2 w-auto h-auto"
															/>
														</div>
													);
												}}
											</form.Field>

											{/* Phone Input */}
											<div className="flex-1">
												<RampsInput
													label={t`Phone Number`}
													type="tel"
													value={phoneField.state.value}
													onChange={(e) => {
														const countryCode =
															form.getFieldValue("phoneCountryCode");
														const formatted = formatPhoneNumber(
															e.target.value,
															countryCode,
														);
														// Set the formatted value - this will trigger onChange validation automatically
														phoneField.handleChange(formatted);
														// Reset verification fields when phone number changes
														setShowVerificationFields(false);
														setVerificationError(null);
														form.setFieldValue("firstName", "");
														form.setFieldValue("lastName", "");
														form.setFieldValue("idLast4", "");
														rampsActions.setPhoneLookupCompleted(false);
														rampsActions.setDigitalProfileId("");
														setIsVerificationRequired(false);
														setIsIdVerified(false);
													}}
													onBlur={phoneField.handleBlur}
													// Don't pass error to avoid UI displacement
												/>
											</div>
										</div>
										{/* Error display - below input, same field instance ensures immediate updates */}
										{error ? (
											<div className="flex items-start">
												<RampsText
													variant="body-sm"
													color="error"
													className="text-left"
												>
													*
													{typeof error === "string"
														? error
														: error &&
																typeof error === "object" &&
																"message" in error
															? (error as { message: string }).message
															: String(error)}
												</RampsText>
											</div>
										) : null}
										{/* Disclaimer text - right below error or input */}
										<RampsText
											variant="body-sm"
											color="secondary"
											className="text-left leading-relaxed"
										>
											{t`Message and data rates may apply.`}
										</RampsText>
									</div>
								);
							}}
						</form.Field>

						{/* Verification Fields for Returning Users */}
						{showVerificationFields && (
							<div className="mt-6 space-y-4 border-ramps-border-default border-t pt-6">
								<RampsText
									variant="body-default"
									color="primary"
									className="mb-4 text-left"
								>
									{t`Please verify your identity to continue`}
								</RampsText>

								<form.Field
									name="firstName"
									validators={{
										onChange: phoneVerificationSchema.shape.firstName,
									}}
								>
									{(field) => (
										<RampsInput
											label={t`First Name`}
											type="text"
											maxLength={FIRST_NAME_FIELD_MAX}
											value={field.state.value}
											onChange={(e) => {
												if (verificationError) setVerificationError(null);
												field.handleChange(formatNameInput(e.target.value));
											}}
											onBlur={field.handleBlur}
											error={getFieldError("firstName")}
										/>
									)}
								</form.Field>

								<form.Field
									name="lastName"
									validators={{
										onChange: phoneVerificationSchema.shape.lastName,
									}}
								>
									{(field) => (
										<RampsInput
											label={t`Last Name`}
											type="text"
											maxLength={NAME_FIELD_MAX}
											value={field.state.value}
											onChange={(e) => {
												if (verificationError) setVerificationError(null);
												field.handleChange(formatNameInput(e.target.value));
											}}
											onBlur={field.handleBlur}
											error={getFieldError("lastName")}
										/>
									)}
								</form.Field>

								<form.Field
									name="idLast4"
									validators={{
										onChange: phoneVerificationSchema.shape.idLast4,
									}}
								>
									{(field) => (
										<RampsInput
											label={t`Last 4 digits of ID`}
											type="text"
											value={field.state.value}
											onChange={(e) => {
												if (verificationError) setVerificationError(null);
												field.handleChange(formatIdLast4(e.target.value));
											}}
											onBlur={field.handleBlur}
											error={getFieldError("idLast4")}
											maxLength={4}
										/>
									)}
								</form.Field>

								{/* Verification Error Message */}
								{verificationError && (
									<div className="mt-2">
										<RampsText
											variant="body-sm"
											color="error"
											className="text-left"
										>
											{verificationError}
										</RampsText>
									</div>
								)}
							</div>
						)}
					</div>
				</div>

				{/* Continue Button */}
				<div className="pt-8 pb-6">
					<form.Subscribe
						selector={(state) => {
							if (showVerificationFields) {
								return phoneVerificationSchema.safeParse(state.values).success;
							}
							return phoneStepSchema.safeParse(state.values).success;
						}}
					>
						{(isStepValid) => (
							<RampsButton
								onClick={handleSubmit}
								disabled={!isStepValid || isCheckingProfile}
								className="h-12 w-full rounded-lg bg-black font-medium text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
							>
								{isCheckingProfile ? t`Checking...` : t`Continue`}
							</RampsButton>
						)}
					</form.Subscribe>
				</div>
			</div>
		</div>
	);
}

export const Route = createFileRoute("/ramps/phone")({
	validateSearch: (search) =>
		search as {
			step?: string;
			transaction_id?: string;
			token?: string;
			accesstoken?: string;
		},
	component: PhoneNumberPage,
});
